import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Receipt, Wallet, TrendingUp, ArrowUpRight, ArrowDownRight, Minus, PieChart } from "lucide-react";
import api from "../lib/apiClient";
import { fmtCAD } from "../lib/format";

export default function MyMoney() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("aftertax"); // aftertax | pretax

  useEffect(() => {
    api.get("/my-money/summary").then(({ data }) => setD(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading your money…</div>;
  if (!d) return null;

  return (
    <div data-testid="my-money-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">My Money</h1>
          <p className="text-sm text-slate-500 mt-1">Year-to-date {d.year} · updated {new Date(d.as_of).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })}</p>
        </div>
        <div className="inline-flex border border-slate-300 rounded-md text-sm self-start">
          <button onClick={() => setView("aftertax")} data-testid="mymoney-view-aftertax" className={`px-3 py-1.5 rounded-l-md ${view === "aftertax" ? "bg-[#0F4C81] text-white" : "text-slate-600 hover:bg-slate-50"}`}>After-tax</button>
          <button onClick={() => setView("pretax")} data-testid="mymoney-view-pretax" className={`px-3 py-1.5 rounded-r-md ${view === "pretax" ? "bg-[#0F4C81] text-white" : "text-slate-600 hover:bg-slate-50"}`}>Before-tax</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CorpTaxBox data={d.corp_tax} />
        <HstBox data={d.hst} />
        <ExpensesBox data={d.expenses} />
        <RemainderBox data={d.remainder} view={view} />
      </div>
    </div>
  );
}

function MoneyBox({ title, icon: Icon, value, sub, trend, color, action, testid, children }) {
  return (
    <Link to={action} data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-md hover:border-slate-300 transition-all group">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded flex items-center justify-center" style={{ background: color + "15", color }}>
            <Icon size={17}/>
          </div>
          <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wider">{title}</h3>
        </div>
        <ArrowUpRight size={14} className="text-slate-300 group-hover:text-[#0F4C81] transition-colors"/>
      </div>
      <div className="font-display text-3xl sm:text-4xl font-bold tabular mt-3" style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      {trend && <TrendBadge trend={trend} className="mt-2"/>}
      {children && <div className="mt-3 pt-3 border-t border-slate-100">{children}</div>}
    </Link>
  );
}

function TrendBadge({ trend, className = "" }) {
  if (!trend || trend.pct === null) return null;
  const up = trend.direction === "up";
  const down = trend.direction === "down";
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const color = up ? "text-emerald-700 bg-emerald-50" : down ? "text-rose-700 bg-rose-50" : "text-slate-600 bg-slate-50";
  return (
    <div className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded ${color} ${className}`} data-testid="trend-badge">
      <Icon size={11}/> {Math.abs(trend.pct).toFixed(1)}% vs last month
    </div>
  );
}

function CorpTaxBox({ data }) {
  return (
    <MoneyBox
      testid="mymoney-corp-tax"
      title="Corporation Tax Owed"
      icon={Landmark}
      value={fmtCAD(data.balance)}
      sub={`of ${fmtCAD(data.owed_ytd)} owed · ${fmtCAD(data.paid_ytd)} paid YTD`}
      color="#0F4C81"
      action="/reporting"
    >
      <div className="text-xs text-slate-500 flex justify-between">
        <span>Net profit YTD</span><span className="tabular text-slate-700 font-medium">{fmtCAD(data.net_profit_ytd)}</span>
      </div>
      <div className="text-xs text-slate-500 flex justify-between mt-1">
        <span>Rate</span><span className="tabular text-slate-700 font-medium">{data.rate.toFixed(2)}%</span>
      </div>
    </MoneyBox>
  );
}

function HstBox({ data }) {
  return (
    <MoneyBox
      testid="mymoney-hst"
      title="HST Owed to CRA"
      icon={Receipt}
      value={fmtCAD(data.owed_ytd)}
      sub={`Collected ${fmtCAD(data.collected_ytd)} · Paid ${fmtCAD(data.paid_ytd)} (ITC)`}
      color="#10B981"
      action="/expenses"
      trend={data.trend}
    >
      <div className="text-xs text-slate-500 flex justify-between">
        <span>This month owed</span><span className="tabular text-slate-700 font-medium">{fmtCAD(data.owed_mtd)}</span>
      </div>
    </MoneyBox>
  );
}

function ExpensesBox({ data }) {
  return (
    <MoneyBox
      testid="mymoney-expenses"
      title="Expenses YTD"
      icon={Wallet}
      value={fmtCAD(data.ytd)}
      sub={`${fmtCAD(data.mtd)} this month · was ${fmtCAD(data.prev_month)} last month`}
      color="#F59E0B"
      action="/expenses"
      trend={data.trend}
    >
      {data.top_5?.length > 0 ? (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><PieChart size={10}/> Top categories</div>
          {data.top_5.map((c) => {
            const pct = data.ytd > 0 ? (c.amount / data.ytd) * 100 : 0;
            return (
              <div key={c.category} className="flex items-center gap-2 text-xs">
                <span className="text-slate-600 truncate flex-1">{c.category}</span>
                <span className="tabular text-slate-500 w-12 text-right">{pct.toFixed(0)}%</span>
                <span className="tabular text-slate-900 font-medium w-20 text-right">{fmtCAD(c.amount)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-slate-400 italic">No expenses recorded yet</div>
      )}
    </MoneyBox>
  );
}

function RemainderBox({ data, view }) {
  const val = view === "aftertax" ? data.aftertax_ytd : data.pretax_ytd;
  const label = view === "aftertax" ? "after all taxes & expenses" : "before tax obligations";
  return (
    <MoneyBox
      testid="mymoney-remainder"
      title="What's Left (Remainder)"
      icon={TrendingUp}
      value={fmtCAD(val)}
      sub={label}
      color={val >= 0 ? "#7C3AED" : "#DC2626"}
      action="/reporting"
      trend={data.trend}
    >
      <div className="text-xs text-slate-500 flex justify-between">
        <span>Before tax YTD</span><span className="tabular text-slate-700 font-medium">{fmtCAD(data.pretax_ytd)}</span>
      </div>
      <div className="text-xs text-slate-500 flex justify-between mt-1">
        <span>After tax YTD</span><span className="tabular text-slate-700 font-medium">{fmtCAD(data.aftertax_ytd)}</span>
      </div>
      <div className="text-xs text-slate-500 flex justify-between mt-1">
        <span>This month (pre-tax)</span><span className="tabular text-slate-700 font-medium">{fmtCAD(data.pretax_mtd)}</span>
      </div>
    </MoneyBox>
  );
}
